<template>
  <div class="home">
    <div :class="{ 'search-centered': !hasResults, 'search-top': hasResults }">
      <form @submit.prevent="() => performSearch(false)" class="search-form">
        <a href="/">
          <img src="/img/logo_mini.png" alt="Mini Logo" class="mini-logo">
        </a>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Enter your search query"
          class="search-input"
        >
        <button type="submit" class="search-button">Search</button>
      </form>
      <div v-if="hasResults" class="search-tabs">
        <button 
          v-for="tab in searchTabs" 
          :key="tab"
          :class="['tab-button', { active: activeTab === tab }]"
          @click="() => activeTab = tab"
        >
          {{ tab }}
        </button>
      </div>
    </div>

    <div v-if="hasResults" class="search-results">
      <!-- Result page -->
      <div v-if="responseData.page === 'result'" class="result-view">
        <h2>Search Results</h2>
        <!-- Image results -->
        <div v-if="responseData.st === 'images' && responseData.result" class="image-grid">
          <div v-for="(result, index) in responseData.result" :key="index" class="image-item">
            <img 
              :src="result.imgurl" 
              :alt="result.title" 
              class="result-image"
              @click="openFullScreenImage(result)"
            >
          </div>
          <div v-intersect="fetchMoreImages" ref="sentinel" class="sentinel"></div>
        </div>
        
        <!-- Full screen image viewer component -->
        <FullscreenImage 
          :image="fullScreenImage" 
          :get-next-image="getNextImage"
          :get-previous-image="getPreviousImage"
          @close="closeFullScreenImage"
          @change-image="changeFullScreenImage"
        />
        
        <!-- Regular results -->
        <div v-if="responseData.result && responseData.st !== 'images'" class="results-list">
          <div v-for="(result, index) in responseData.result" :key="index" class="result-item">
            <div class="result-content">
              <a :href="result.click ? `/v1/api/click?${result.click}` : result.url" class="result-title" target="_blank">{{ result.title }}</a>
              <div class="result-url">{{ result.url }}</div>
              <div class="result-headline">{{ result.headline }}</div>
              <div class="result-description">{{ result.description }}</div>
            </div>
            <img v-if="result.photo" :src="result.photo" class="result-photo" :alt="result.title">
          </div>
        </div>
        
        <!-- Pagination -->
        <div v-if="responseData.pages > 1" class="pagination">
          <button 
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
            class="page-button"
          >
            Previous
          </button>
          
          <div class="page-numbers">
            <button 
              v-for="page in pageNumbers" 
              :key="page"
              @click="changePage(page)"
              :class="['page-number', { active: page === currentPage }]"
            >
              {{ page == -1 ? "..." : page }}
            </button>
          </div>
          
          <button 
            :disabled="currentPage === responseData.pages"
            @click="changePage(currentPage + 1)"
            class="page-button"
          >
            Next
          </button>
        </div>
      </div>
      
      <!-- Normal/Abuse page -->
      <div v-else class="message-view">
        <h2>{{ responseData.page === 'normal' ? 'Normal Page' : 'Abuse Detected' }}</h2>
        <div class="query-display">
          Your search query: "{{ searchQuery }}"

          <div v-if="responseData.page === 'normal'">
            <button @click="searchQwant">QWANT</button>
            <button @click="searchGoogle">Google</button>
            <button @click="searchBing">Bing</button>
            
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { 
  defineComponent, 
  ref, 
  computed, 
  onMounted, 
  watch,
  onBeforeUnmount
 } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { intersect } from '@/helper/intersect'
import FullscreenImage, { ImageItem } from '@/components/FullscreenImage.vue';

// Register the intersect directive
const directives = { intersect };

interface SearchResult {
  title: string;
  url: string;
  click?: string;
  headline: string;
  description: string;
  imgurl?: string;
  photo?: string;
}

interface SearchResponse {
  page: string;
  num: number;
  seed: number;
  pages: number;
  token: string;
  photo?: string;
  st?: string;
  result?: SearchResult[];
}

export default defineComponent({
  name: 'HomeView',
  components: {
    FullscreenImage
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const searchQuery = ref('');
    const responseData = ref<SearchResponse>({ page: '', seed:0, num: 1, pages: 0, token: '' });
    const currentPage = ref(1);
    const searchTabs = ['All', 'Images'];
    const activeTab = ref('All');
    const sentinel = ref<HTMLDivElement | null>(null);
    const fullScreenImage = ref<SearchResult | null>(null);
    
    // Declare the observer.
    let observer: IntersectionObserver | null = null;


    const getSearchType = (tab: string): string => {
      return tab.toLowerCase();
    };

    // Watch for tab changes and trigger search
    watch(activeTab, () => {
      if (searchQuery.value.trim()) {
        performSearch(false);
      }
    });

    const hasResults = computed(() => responseData.value.page !== '');

    const performSearch = async (append = false) => {
      console.log('performSearch', append)
      if (!searchQuery.value.trim()) return;
      
      try {
        // Reset to first page on new search
        currentPage.value = 1;
        
        // Update URL
        router.push({ 
          path: '/', 
          query: { 
            q: searchQuery.value,
            page: currentPage.value.toString()
          }
        });
        
        // Perform search
        let seed = localStorage.getItem('seed')
        if (!seed) {
          seed = "0"
        }
        const response = await fetch(`/v1/api/search?q=${encodeURIComponent(searchQuery.value)}&seed=${encodeURIComponent(seed)}&num=${currentPage.value}&st=${getSearchType(activeTab.value)}`);
        const text = await response.text(); // Get raw response text first
        try {
          const data = JSON.parse(text); // Try to parse it
          if (append) {
            data.result = [...responseData.value.result ?? [], ...data.result]
          }
          responseData.value = data;
          if (data.page == 'normal' || data.page == 'abuse') {
            const query = searchQuery.value
            searchQuery.value = ''
            responseData.value.page = ''

            router.push({ 
              path: '/', 
            });

            setTimeout(() => {
              document.location.href = `https://www.qwant.com/?q=`+encodeURIComponent(query)
            }, 200)
          }
          if (data.seed) localStorage.setItem('seed', data.seed)
          if (data.token) localStorage.setItem('token', data.token)
        } catch (jsonError) {
          console.error('JSON Parse Error:', jsonError);
          console.error('Raw response:', text);
          throw new Error(`Invalid JSON response: ${text.substring(0, 100)}...`);
        }
      } catch (error) {
        console.error('Search failed:', error);
        responseData.value = { page: '', seed: 0, num: 1, pages: 0, token: '' };
        // alert('Search failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        console.log('Search failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    };

    const fetchMoreImages = async () => {
      // console.log('fetchMoreImages')
      await performSearch(true)
    };

    // Handle initial load with query parameter
    onMounted(() => {
      // console.log('onMounted 1')
      setupObserver();
      const queryParam = route.query.q;
      if (queryParam && typeof queryParam === 'string') {
        searchQuery.value = queryParam;
        performSearch(false);
      }
    });

    const changePage = async (newPage: number) => {
      if (newPage < 1 || newPage > responseData.value.pages) return;
      
      currentPage.value = newPage;
      
      try {
        router.push({ 
          path: '/', 
          query: { 
            q: searchQuery.value,
            page: newPage.toString(),
            token: responseData.value.token
          }
        });
        
        const response = await fetch(
          `/v1/api/search?q=${encodeURIComponent(searchQuery.value)}&num=${newPage}&token=${responseData.value.token}&st=${getSearchType(activeTab.value)}`
        );
        const data = await response.json();
        responseData.value = data;
      } catch (error) {
        console.error('Page change failed:', error);
      }
    };


    const setupObserver = () => {
      // Create an Intersection Observer to watch the sentinel.
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchMoreImages();
          }
        });
      }, {
        root: null,         // relative to the viewport
        rootMargin: '0px',  // no margin around the root
        threshold: 0.1      // trigger when 10% of the sentinel is visible
      });

      if (sentinel.value) {
        observer.observe(sentinel.value);
      }
    };


    // Handle initial load with query parameters
    onMounted(() => {
      // console.log('onMounted 2')
      const queryParam = route.query.q;
      const pageParam = route.query.page;
      const tokenParam = route.query.token;
      
      if (queryParam && typeof queryParam === 'string') {
        searchQuery.value = queryParam;
        if (pageParam && typeof pageParam === 'string') {
          currentPage.value = parseInt(pageParam, 10);
        }
        if (tokenParam && typeof tokenParam === 'string') {
          responseData.value.token = tokenParam;
        }
        performSearch(false);
      }
    });

    onBeforeUnmount(() => {
      if (observer && sentinel.value) {
        observer.unobserve(sentinel.value);
      }
    });

    const pageNumbers = computed(() => {
      const total = responseData.value.pages;
      const current = currentPage.value;
      const delta = 2; // Number of pages to show before and after current page
      
      const pages: number[] = [];
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      
      // Add first page if not included
      if (pages[0] > 1) {
        pages.unshift(1);
        if (pages[1] > 2) pages.splice(1, 0, -1); // Add ellipsis
      }
      
      // Add last page if not included
      if (pages[pages.length - 1] < total) {
        if (pages[pages.length - 1] < total - 1) pages.push(-1); // Add ellipsis
        pages.push(total);
      }
      
      return pages;
    });


    const searchQwant = () => {
      const queryParam = route.query.q;
    
      if (queryParam && typeof queryParam === 'string') {
        document.location.href = `https://www.qwant.com/?q=`+encodeURIComponent(queryParam)
      }
    }

    const searchGoogle = () => {
      const queryParam = route.query.q;
    
      if (queryParam && typeof queryParam === 'string') {
        document.location.href = `https://www.google.com/?q=`+encodeURIComponent(queryParam)
      }      
    }

    const searchBing = () => {
      const queryParam = route.query.q;
    
      if (queryParam && typeof queryParam === 'string') {
        document.location.href = `https://www.bing.com/?q=`+encodeURIComponent(queryParam)
      }      
    }

    // Function to get the next image from the results list
    const getNextImage = (currentImage: SearchResult): SearchResult | null => {
      if (!responseData.value.result) return null;
      
      const currentIndex = responseData.value.result.findIndex(
        img => img.imgurl === currentImage.imgurl
      );
      
      if (currentIndex === -1 || currentIndex === responseData.value.result.length - 1) {
        return null;
      }
      
      return responseData.value.result[currentIndex + 1];
    };
    
    // Function to get the previous image from the results list
    const getPreviousImage = (currentImage: SearchResult): SearchResult | null => {
      if (!responseData.value.result) return null;
      
      const currentIndex = responseData.value.result.findIndex(
        img => img.imgurl === currentImage.imgurl
      );
      
      if (currentIndex <= 0) {
        return null;
      }
      
      return responseData.value.result[currentIndex - 1];
    };

    const openFullScreenImage = (image: SearchResult) => {
      fullScreenImage.value = image;
      document.body.style.overflow = 'hidden'; // Prevent scrolling when overlay is open
    };

    const closeFullScreenImage = () => {
      fullScreenImage.value = null;
      document.body.style.overflow = ''; // Restore scrolling
    };
    
    const changeFullScreenImage = (newImage: SearchResult) => {
      fullScreenImage.value = newImage;
    };

    return {
      searchQuery,
      responseData,
      currentPage,
      hasResults,
      pageNumbers,
      performSearch,
      changePage,
      searchTabs,
      activeTab,
      searchQwant,
      searchGoogle,
      searchBing,
      fetchMoreImages,
      fullScreenImage,
      openFullScreenImage,
      closeFullScreenImage,
      changeFullScreenImage,
      getNextImage,
      getPreviousImage
    };
  }
});
</script>

<style scoped>
.home {
  min-height: 100vh;
}

.search-centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
}

.search-top {
  padding: 2rem 0;
  text-align: center;
}

.logo {
  width: 200px;
  margin-bottom: 2rem;
}

.search-form {
  display: flex;
  gap: 10px;
  max-width: 600px;
  margin: 0 auto;
  align-items: center;
}

.mini-logo {
  width: 72px;
  height: 72px;
  border-radius: 36px;
  object-fit: contain;
}

.search-input {
  flex: 1;
  padding: 12px 20px;
  margin: 5px 60px;
  width: 600px;
  font-size: 16px;
  border: 1px solid #dfe1e5;
  border-radius: 24px;
  outline: none;
}

.search-input:focus {
  box-shadow: 0 1px 6px rgba(32,33,36,0.28);
  border-color: rgba(223,225,229,0);
}

.search-button {
  padding: 0 20px;
  background: #f8f9fa;
  border: 1px solid #f8f9fa;
  border-radius: 4px;
  color: #3c4043;
  cursor: pointer;
  font-size: 14px;
  height: 44px;
}

.search-button:hover {
  box-shadow: 0 1px 1px rgba(0,0,0,.1);
  background-color: #f8f9fa;
  border: 1px solid #dadce0;
  color: #202124;
}

.search-tabs {
  margin-top: 1rem;
  border-bottom: 1px solid #dfe1e5;
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 0 20px;
}

.tab-button {
  background: none;
  border: none;
  padding: 0.5rem 0.25rem;
  color: #5f6368;
  font-size: 14px;
  cursor: pointer;
  position: relative;
  margin-bottom: -1px;
}

.tab-button:hover {
  color: #1a73e8;
}

.tab-button.active {
  color: #1a73e8;
  border-bottom: 3px solid #1a73e8;
}

.search-results {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
  text-align: left;
}

.result-item {
  margin-bottom: 20px;
  padding: 10px 0;
  display: flex;
  gap: 20px;
}

.result-content {
  flex: 1;
}

.result-photo {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
}

.result-title {
  color: #1a0dab;
  font-size: 18px;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;
}

.result-title:hover {
  text-decoration: underline;
}

.result-url {
  color: #006621;
  font-size: 14px;
}

.result-headline {
  font-weight: bold;
  margin: 8px 0;
}

.result-description {
  color: #4d5156;
  font-size: 14px;
  line-height: 1.58;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  padding: 16px;
}

.image-item {
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.image-item:hover {
  transform: scale(1.05);
}

.result-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
  gap: 20px;
}

.page-button {
  padding: 8px 16px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.page-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 5px;
  align-items: center;
  margin: 0 10px;
}

.page-number {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  min-width: 35px;
}

.page-number.active {
  background: #1a73e8;
  color: white;
  border-color: #1a73e8;
}

.page-number:hover:not(.active) {
  background: #f8f9fa;
}

.message-view {
  text-align: center;
  padding: 20px;
}

.query-display {
  font-size: 18px;
  color: #666;
  margin-top: 15px;
}

</style>
